import { useState } from 'react'
import toast from 'react-hot-toast'

import {
  FvButton,
  type IconName,
  useFvNavigate,
  ValidatedForm,
} from '@fv/client-components'
import { useFormModelState } from '@fv/client-core'
import type { FullShipment, Quote } from '@fv/client-types'
import type { QuoteReasonPayload, QuoteSelectionReason } from '@fv/models'
import type { DTO } from '@fv/models/core'

import InfoBox from '@/components/InfoBox'
import { ShipperSlider } from '@/components/ShipperSlider'
import { useQuotes } from '@/hooks/shipments'
import { routes } from '@/routes'

import { useNeedsQuoteReason, useUpdateQuoteReason } from './hooks'
import { QuoteReasonFormControls } from './QuoteReasonFormControls'

type QuoteReasonFormProps = {
  onCancel?: () => void
  onSuccess: (shipment: FullShipment) => void
  loadId: string
  value?: DTO<QuoteSelectionReason>
  cancelText?: string
  cancelIcon?: IconName
}
export const QuoteReasonForm = ({
  loadId,
  onCancel,
  onSuccess,
  value: initialValue,
  cancelText = 'Cancel',
  cancelIcon = 'times',
}: QuoteReasonFormProps) => {
  const updateQuoteReason = useUpdateQuoteReason(loadId)
  const { value: formValue, setValue: updateFormValue } =
    useFormModelState<QuoteReasonPayload>({
      initialValue: {
        reasonType: initialValue?.reasonType ?? 'urgent',
        description: initialValue?.description ?? '',
      },
    })

  const handleSubmit = async () => {
    try {
      const shipment = await updateQuoteReason.mutateAsync(formValue)
      onSuccess(shipment)
    } catch {
      toast.error('There was an error saving your response')
    }
  }

  return (
    <div>
      <InfoBox>
        Please indicate why you have not chosen the cheapest quote.
      </InfoBox>
      <ValidatedForm onValidSubmit={handleSubmit}>
        <QuoteReasonFormControls value={formValue} onChange={updateFormValue} />
        <div className="flex">
          <FvButton
            type="submit"
            icon="check"
            theme="primary"
            loading={updateQuoteReason.isPending}
          >
            Save and proceed
          </FvButton>
          <FvButton icon={cancelIcon} onClick={onCancel} theme="plain">
            {cancelText}
          </FvButton>
        </div>
      </ValidatedForm>
    </div>
  )
}

type QuoteReasonSliderProps = {
  load: Pick<
    FullShipment,
    'workflow' | 'loadId' | 'quoteRequestId' | 'quoteSelectionReason' | 'status'
  >
  quote: Pick<Quote, 'amount'>
}
export const QuoteReasonSlider = ({ quote, load }: QuoteReasonSliderProps) => {
  const navigate = useFvNavigate()
  const needsQuoteReason = useNeedsQuoteReason()
  const quotes = useQuotes(load.loadId)
  const [submitted, setSubmitted] = useState(false)
  const canSkip =
    load.status !== 'pending' && !!load.quoteSelectionReason?.reasonType

  if (!quotes.data?.length) {
    return null
  }

  const needsForm = needsQuoteReason(quotes.data ?? [], quote)
  if (!needsForm) {
    return null
  }

  return (
    <ShipperSlider position="right" isOpen={!submitted}>
      <QuoteReasonForm
        loadId={load.loadId}
        value={load.quoteSelectionReason}
        onCancel={() =>
          canSkip ? setSubmitted(true) : navigate(routes.rates(load))
        }
        onSuccess={() => setSubmitted(true)}
        cancelText={canSkip ? 'Close' : 'Take me back to my rates'}
        cancelIcon="arrow-left"
      />
    </ShipperSlider>
  )
}
